import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import TopNav from "../components/TopNav";
import { User, Key, Mail, Settings2, Plus, Trash2, Check, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function ProfileTab({ user, refreshUser }) {
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.patch(`${API}/users/me`, { name }, { withCredentials: true });
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-black mb-4"
          style={{ background: "linear-gradient(135deg, var(--accent-gold), var(--accent-gold))" }}>
          {(user?.name || user?.email || "U")[0].toUpperCase()}
        </div>
        <div className="text-[var(--ink-tertiary)] text-sm">
          <span className="capitalize text-[var(--accent-gold)] font-semibold">{user?.subscription_tier}</span> tier
          · {user?.credits_balance || 0} credits
        </div>
      </div>
      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="text-[var(--ink-secondary)] text-sm block mb-1.5">Display Name</label>
          <input value={name} onChange={e => setName(e.target.value)} data-testid="profile-name-input"
            className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
            style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }} />
        </div>
        <div>
          <label className="text-[var(--ink-secondary)] text-sm block mb-1.5">Email Address</label>
          <input value={user?.email || ""} disabled
            className="w-full px-4 py-2.5 rounded-xl text-[var(--ink-tertiary)] text-sm outline-none"
            style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }} />
        </div>
        <button type="submit" disabled={saving} data-testid="save-profile-button"
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all"
          style={{ background: saving ? "var(--surface-card)" : "var(--accent-gold)" }}>
          {saving ? "Saving..." : saved ? <><Check size={14} /> Saved!</> : "Save Changes"}
        </button>
      </form>
    </div>
  );
}

function ApiKeysTab() {
  const [keys, setKeys] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ provider: "anthropic", key: "", label: "" });
  const [adding, setAdding] = useState(false);
  const [validatingId, setValidatingId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const loadKeys = () => axios.get(`${API}/users/me/api-keys`, { withCredentials: true }).then(r => setKeys(r.data));

  useEffect(() => { loadKeys(); }, []);

  const handleValidate = async (keyId) => {
    setValidatingId(keyId);
    try {
      const response = await axios.post(`${API}/users/me/api-keys/${keyId}/validate`, {}, { withCredentials: true });
      setFeedback({ type: response.data.is_valid ? "success" : "error", text: response.data.message });
      await loadKeys();
    } catch (err) {
      setFeedback({ type: "error", text: err.response?.data?.detail || "Unable to validate the API key." });
    } finally {
      setValidatingId(null);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const saveResponse = await axios.post(`${API}/users/me/api-keys`, form, { withCredentials: true });
      await handleValidate(saveResponse.data.id);
      await loadKeys();
      setShowAdd(false);
      setForm({ provider: "anthropic", key: "", label: "" });
    } catch (err) {
      console.error(err);
      setFeedback({ type: "error", text: err.response?.data?.detail || "Unable to save API key." });
    } finally { setAdding(false); }
  };

  const handleDelete = async (keyId) => {
    await axios.delete(`${API}/users/me/api-keys/${keyId}`, { withCredentials: true });
    setKeys(keys.filter(k => k.id !== keyId));
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[var(--ink-secondary)] text-sm">Store your own API keys to use BYOK (Bring Your Own Key) generation</p>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} data-testid="add-api-key-button"
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium"
          style={{ background: "var(--accent-gold)" }}>
          <Plus size={14} />
          Add Key
        </button>
      </div>

      {showAdd && (
        <form onSubmit={handleAdd} className="rounded-xl p-5 mb-5 space-y-4"
          style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[var(--ink-secondary)] text-xs block mb-1.5">Provider</label>
              <select value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }}>
                <option value="anthropic">Anthropic</option>
                <option value="openai">OpenAI</option>
                <option value="google">Google</option>
              </select>
            </div>
            <div>
              <label className="text-[var(--ink-secondary)] text-xs block mb-1.5">Label</label>
              <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                placeholder="My Anthropic Key" required data-testid="api-key-label-input"
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }} />
            </div>
          </div>
          <div>
            <label className="text-[var(--ink-secondary)] text-xs block mb-1.5">API Key</label>
            <input type="password" value={form.key} onChange={e => setForm({ ...form, key: e.target.value })}
              placeholder="sk-..." required data-testid="api-key-value-input"
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }} />
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={adding} data-testid="save-api-key-button"
              className="px-5 py-2 rounded-lg text-sm font-medium"
              style={{ background: "var(--ce-08-valor)" }}>
              {adding ? "Saving..." : "Validate & Save"}
            </button>
            <button type="button" onClick={() => setShowAdd(false)}
              className="px-5 py-2 rounded-lg text-[var(--ink-secondary)] text-sm"
              style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {feedback && (
        <div
          className="rounded-xl px-4 py-3 mb-5 text-sm"
          data-testid="api-key-feedback"
          style={{
            background: feedback.type === "success" ? "rgba(122,144,112,0.1)" : "rgba(184,117,107,0.1)",
            border: `1px solid ${feedback.type === "success" ? "rgba(122,144,112,0.3)" : "rgba(184,117,107,0.3)"}`,
            color: feedback.type === "success" ? "var(--semantic-success)" : "var(--semantic-error)",
          }}
        >
          {feedback.text}
        </div>
      )}

      {keys.length === 0 ? (
        <div className="rounded-xl p-8 text-center" style={{ border: "1px dashed var(--border-subtle)" }}>
          <Key size={32} className="text-[var(--border-subtle)] mx-auto mb-3" />
          <p className="text-[var(--ink-tertiary)] text-sm">No API keys added yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {keys.map(k => (
            <div key={k.id} className="flex items-center justify-between rounded-xl px-4 py-3"
              data-testid={`api-key-card-${k.id}`}
              style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
              <div className="flex items-center gap-3">
                <div className="text-[var(--ink-secondary)] text-xs px-2 py-1 rounded-lg capitalize"
                  style={{ background: "var(--surface-page)" }}>{k.provider}</div>
                <div>
                  <div className="text-sm font-medium">{k.label}</div>
                  <div className="text-[var(--ink-tertiary)] text-xs font-mono">{k.encrypted_key}</div>
                  {k.last_validated_at && (
                    <div className="text-[var(--ink-tertiary)] text-[11px] mt-1">Validated {new Date(k.last_validated_at).toLocaleString()}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs flex items-center gap-1" style={{ color: k.is_valid ? "var(--ce-08-valor)" : "var(--ce-01-romance)" }}>
                  {k.is_valid ? <Check size={12} /> : <X size={12} />}
                  {k.is_valid ? "Valid" : "Invalid"}
                </span>
                <button
                  onClick={() => handleValidate(k.id)}
                  data-testid={`validate-api-key-${k.id}`}
                  className="text-[var(--ink-secondary)] hover: text-xs px-3 py-1.5 rounded-lg transition-colors"
                  style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }}
                >
                  {validatingId === k.id ? "Checking..." : "Recheck"}
                </button>
                <button onClick={() => handleDelete(k.id)} data-testid={`delete-api-key-${k.id}`}
                  className="text-[var(--semantic-error)] hover:bg-[rgba(184,117,107,0.1)] p-1.5 rounded-lg transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactTab() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent("ArcScript AI Inquiry");
    const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`);
    window.open(`mailto:samvastava@arc-script.com?subject=${subject}&body=${body}`, "_blank");
    setSent(true);
    setTimeout(() => setSent(false), 3000);
  };

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Interested in ArcScript?</h2>
        <p className="text-[var(--ink-secondary)] text-sm leading-relaxed">
          ArcScript's emotional intelligence engine is available for acquisition or licensing.
          Get in touch to discuss how it can power your production pipeline.
        </p>
      </div>

      <div className="rounded-xl p-4 flex items-center gap-3"
        style={{ background: "rgba(176,141,87,0.08)", border: "1px solid rgba(176,141,87,0.25)" }}>
        <Mail size={16} style={{ color: "var(--accent-gold)", flexShrink: 0 }} />
        <a href="mailto:samvastava@arc-script.com?subject=ArcScript%20AI%20Inquiry"
          data-testid="contact-email-link"
          className="hover:underline"
          style={{ color: "var(--accent-gold)", fontSize: "0.95rem", fontWeight: 600 }}>
          samvastava@arc-script.com
        </a>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[var(--ink-secondary)] text-sm block mb-1.5">Your Name</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Smith" required data-testid="contact-name-input"
              className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
              style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)", color: "var(--ink-primary)" }} />
          </div>
          <div>
            <label className="text-[var(--ink-secondary)] text-sm block mb-1.5">Your Email</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="jane@studio.com" required data-testid="contact-email-input"
              className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
              style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)", color: "var(--ink-primary)" }} />
          </div>
        </div>
        <div>
          <label className="text-[var(--ink-secondary)] text-sm block mb-1.5">Message</label>
          <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
            rows={5} required data-testid="contact-message-input"
            placeholder="Tell us about your project, studio, or integration need..."
            className="w-full px-4 py-2.5 rounded-xl text-sm outline-none resize-none"
            style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)", color: "var(--ink-primary)" }} />
        </div>
        <button type="submit" data-testid="contact-submit-button"
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all"
          style={{ background: "var(--accent-gold)", color: "var(--surface-page)" }}>
          {sent ? <><Check size={14} /> Message Sent!</> : "Send Inquiry"}
        </button>
      </form>
    </div>
  );
}

function PreferencesTab({ user }) {
  const [defaultModel, setDefaultModel] = useState("claude-sonnet-4-6");
  const [defaultPlatform, setDefaultPlatform] = useState("general");

  return (
    <div className="max-w-lg space-y-5">
      <div>
        <label className="text-[var(--ink-secondary)] text-sm block mb-1.5">Default AI Model</label>
        <select value={defaultModel} onChange={e => setDefaultModel(e.target.value)}
          className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
          style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
          <option value="claude-sonnet-4-6">Claude Sonnet 4.6 (Recommended)</option>
          <option value="claude-opus-4-6">Claude Opus 4.6 (Premium)</option>
          <option value="gpt-4o">GPT-4o (Fast)</option>
          <option value="gpt-5-4">GPT-5.4 (Premium)</option>
          <option value="gemini-3-pro">Gemini 3 Pro (Multilingual)</option>
        </select>
      </div>
      <div>
        <label className="text-[var(--ink-secondary)] text-sm block mb-1.5">Default Platform</label>
        <select value={defaultPlatform} onChange={e => setDefaultPlatform(e.target.value)}
          className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
          style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
          {["reelshort", "dramabox", "flextv", "goodshort", "general"].map(p => (
            <option key={p} value={p} className="capitalize">{p.charAt(0).toUpperCase() + p.slice(1)}</option>
          ))}
        </select>
      </div>
      <button className="px-6 py-2.5 rounded-xl font-semibold text-sm"
        style={{ background: "var(--accent-gold)" }}>
        Save Preferences
      </button>
    </div>
  );
}

const TABS = [
  { id: "profile", label: "Profile", icon: User },
  { id: "api-keys", label: "API Keys", icon: Key },
  { id: "contact", label: "Contact", icon: Mail },
  { id: "preferences", label: "Preferences", icon: Settings2 },
];

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState("profile");
  const isDemo = user?.email === "demo@arcscript.ai";
  const visibleTabs = TABS.filter(t => !(isDemo && t.id === "api-keys"));

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-page)" }}>
      <TopNav />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-black tracking-tight mb-8">Settings</h1>

        <div className="flex gap-6">
          {/* Sidebar tabs */}
          <div className="w-48 flex-shrink-0">
            <div className="space-y-1">
              {visibleTabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    data-testid={`settings-tab-${tab.id}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-left transition-all"
                    style={activeTab === tab.id
                      ? { background: "rgba(176,141,87,0.18)", color: "var(--ink-primary)", borderLeft: "2px solid var(--accent-gold)" }
                      : { color: "var(--ink-secondary)" }}>
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <motion.div key={activeTab} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}>
              {activeTab === "profile" && <ProfileTab user={user} refreshUser={refreshUser} />}
              {activeTab === "api-keys" && <ApiKeysTab />}
              {activeTab === "contact" && <ContactTab />}
              {activeTab === "preferences" && <PreferencesTab user={user} />}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
